/**
 * 3D model API client (EAA-1642).
 *
 * Upload uses the tenant JWT / API token via sharedApiClient. The serve URL is
 * a one-time, short-lived link minted per model load — never store it.
 *
 * Staff (non-tenant) JWTs carry no tenant, so — like the web-content services —
 * we pass `tenant_id` explicitly, read best-effort from the current URL. When a
 * tenant-scoped or embed Session JWT is used, the backend ignores it.
 */

import { sharedApiClient } from '@ai-content-hub/shared-api';
import type { Model3DFormat } from './schema';

export interface Model3DUploadResult {
    asset_id: string;
    file_format: Model3DFormat;
    file_size: number;
    original_filename: string;
    status: string;
}

/**
 * EAA-1642: the model's structure, extracted client-side once and stored on
 * the asset itself (dedicated `animations` / `objects` columns, not a metadata
 * bag) so no later editor session re-downloads and re-parses a 50-100MB GLB.
 */
export interface Model3DOutlineResult {
    /** AnimationClip names, in file order. */
    animations: string[];
    /** Names of nodes a learner can click (anything with geometry beneath it). */
    objects: string[];
}

export interface Model3DTokenResult {
    /** Absolute, one-time, short-lived URL to fetch the model bytes. */
    url: string;
    expires_in: number;
    file_format: Model3DFormat;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ── Module-level tenant context ──────────────────────────────────────────────
// Set once by Content3DEdit / Content3DPlayer on mount so every API call
// (upload, token, delete) can attach ?tenant_id= without threading the prop
// through intermediate helper functions like loadModel3D → fetchModelBytes.
let _tenantId: string | null = null;

/**
 * Set the module-level tenant ID. Call from the editor/player component on
 * mount (or whenever the tenantId prop changes).
 */
export function setModel3DTenantId(id: string | undefined | null): void {
    _tenantId = id && UUID_RE.test(id) ? id : null;
}

/** Best-effort current tenant id, used for staff JWTs.
 *
 * Resolution order:
 *  1. Module-level store (set by the component via setModel3DTenantId)
 *  2. URL search params (?tenant_id=…) — works in production embed iframes
 */
function currentTenantId(): string | null {
    // 1. Module-level (set by component prop)
    if (_tenantId) return _tenantId;
    // 2. URL search params (production embed iframe has ?tenant_id=)
    if (typeof window === 'undefined') return null;
    try {
        const id = new URLSearchParams(window.location.search).get('tenant_id');
        return id && UUID_RE.test(id) ? id : null;
    } catch {
        return null;
    }
}

function withTenant(endpoint: string): string {
    const tenantId = currentTenantId();
    if (!tenantId) return endpoint;
    return endpoint + (endpoint.includes('?') ? '&' : '?') + `tenant_id=${tenantId}`;
}

/**
 * Upload a .glb/.gltf model. `contentId` is optional — a brand-new content has
 * no persisted UUID yet, so the asset is created unlinked and referenced by id.
 * POST /api/content/model3d/upload?tenant_id=...
 *
 * tenant_id is required for staff/superadmin JWTs which carry no tenant on the
 * token. Pass it as a parameter; if unavailable, falls back to URL search params.
 */
export async function uploadModel3D(file: File, contentId?: string, tenantId?: string): Promise<Model3DUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (contentId && UUID_RE.test(contentId)) {
        formData.append('content_id', contentId);
    }

    // Resolve tenant_id: explicit param > URL search params > empty (let server use JWT context)
    let endpoint = '/content/model3d/upload';
    const resolvedTenantId = tenantId || currentTenantId();
    if (resolvedTenantId) {
        endpoint += `?tenant_id=${resolvedTenantId}`;
        console.debug(`[content-3d] Upload with tenant_id: ${resolvedTenantId}`);
    } else {
        console.debug('[content-3d] Upload without explicit tenant_id; server will use JWT context');
    }

    // sharedApiClient rejects with an ApiError (has `.message`) on non-2xx.
    const res = await sharedApiClient.postFormData<Model3DUploadResult>(
        endpoint,
        formData,
    );
    return res.data;
}

/**
 * Mint a fresh one-time serve URL for a 3D model asset.
 * POST /api/content/model3d/{assetId}/token
 *
 * The returned URL is single-use: fetching it consumes the token server-side,
 * so a new token must be requested for every (re)load or retry.
 */
export async function requestModel3DToken(assetId: string): Promise<Model3DTokenResult> {
    const res = await sharedApiClient.post<Model3DTokenResult>(
        withTenant(`/content/model3d/${assetId}/token`),
    );
    return res.data;
}

/**
 * Delete a stored 3D model asset.
 * DELETE /api/content/model3d/{assetId}/asset
 */
export async function deleteModel3D(assetId: string): Promise<boolean> {
    const res = await sharedApiClient.delete(withTenant(`/content/model3d/${assetId}/asset`));
    return res.ok;
}

/**
 * Upload an .hdr environment map for the 3D viewer.
 * POST /api/content/model3d/upload?tenant_id=...
 * Uses the same endpoint as model upload but with asset_type=environment.
 */
export async function uploadModel3DEnvironment(file: File, contentId?: string, tenantId?: string): Promise<Model3DUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', 'environment');
    if (contentId && UUID_RE.test(contentId)) {
        formData.append('content_id', contentId);
    }

    let endpoint = '/content/model3d/upload';
    const resolvedTenantId = tenantId || currentTenantId();
    if (resolvedTenantId) {
        endpoint += `?tenant_id=${resolvedTenantId}`;
    }

    const res = await sharedApiClient.postFormData<Model3DUploadResult>(
        endpoint,
        formData,
    );
    return res.data;
}

/**
 * Read the stored outline for an asset.
 * GET /api/content/model3d/{assetId}/outline
 *
 * Returns empty lists when the asset predates this feature — the caller then
 * falls back to parsing the model once and calling `saveModel3DOutline`.
 */
export async function fetchModel3DOutline(assetId: string): Promise<Model3DOutlineResult> {
    const res = await sharedApiClient.get<Model3DOutlineResult>(
        withTenant(`/content/model3d/${assetId}/outline`),
    );
    return {
        animations: res.data?.animations ?? [],
        objects: res.data?.objects ?? [],
    };
}

/**
 * Persist the extracted outline onto the asset.
 * PATCH /api/content/model3d/{assetId}/outline
 *
 * Best-effort by design: the editor has the names in hand either way, so a
 * failure here costs a rescan next time, never the authoring session.
 */
export async function saveModel3DOutline(
    assetId: string,
    outline: Model3DOutlineResult,
): Promise<boolean> {
    try {
        const res = await sharedApiClient.patch(
            withTenant(`/content/model3d/${assetId}/outline`),
            { animations: outline.animations, objects: outline.objects },
        );
        return res.ok;
    } catch (err) {
        console.warn('[content-3d] Could not store the model outline on the asset:', err);
        return false;
    }
}
